/**
 * "Klient" — who a new session or series is with. Once chosen it is one row (the disc, the name,
 * "Zmień"), the canon's field; before that, and after "Zmień", it is the app's one-of-N picker over
 * the roster (`shared/ui/PickerList`), filtered by `entities/client`'s own `assignableClients` —
 * the active and the invited clients a trainer can book for, and nobody else.
 *
 * Owns only whether the list is open. Which client is chosen is the draft's. While the trainer runs
 * credits (R18) the chosen client's standing stands beside the name — their credits, or the sessions
 * they have to settle (R16) — read through the same `clientStanding` the session panel reads.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { assignableClients, matchingClients, rosterQueryOptions } from '~/entities/client';
import { displayInitials } from '~/shared/lib/displayInitials';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import choice from '~/shared/ui/choiceRow.module.css';
import { PickerList } from '~/shared/ui/PickerList';
import skeleton from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import { creditSummaryQueryOptions } from '~/features/credit-ledger';

import { clientStanding, readState } from '../model/sessionCredit';

import styles from './ComposeForm.module.css';

interface ClientFieldProps {
  readonly clientId: number | null;
  readonly onChoose: (clientId: number) => void;
  /** The form's reason when nobody is chosen — the search is marked and described by it. */
  readonly reasonId?: string | null;
}

export function ClientField({ clientId, onChoose, reasonId = null }: ClientFieldProps) {
  const { t, i18n } = useTranslation();
  const [changing, setChanging] = useState(false);
  const [query, setQuery] = useState('');
  const roster = useQuery(rosterQueryOptions());
  const credits = useQuery(creditSummaryQueryOptions());
  const summary = readState(credits);
  // The standing's place holds a bar until the summary answers, so it does not pop in beside a name.
  const creditsGate = useSkeletonGate(summary.kind === 'pending');
  const clients = assignableClients(roster.data, i18n.language);
  const chosen =
    clientId === null ? null : (clients.find((client) => client.customerId === clientId) ?? null);

  if (chosen !== null && !changing) {
    const standing = clientStanding(summary, chosen.customerId);
    return (
      <div className={styles.field} data-field="client">
        <span className={styles.label}>{t('calendar.create.clientLabel')}</span>
        <div className={styles.chosen}>
          <AvatarChip initials={displayInitials(chosen.label)} avatarUrl={null} />
          <span className={styles.chosenName}>{chosen.label}</span>
          {creditsGate.waiting ? (
            <span
              className={`${creditsGate.skeleton ? String(skeleton.bar) : ''} ${String(styles.chosenCreditsPending)}`}
              aria-hidden="true"
            />
          ) : standing.kind === 'failed' ? (
            // The panel's rule, quietly: a summary that failed is not credits off. The retry lives
            // on the session panel; here the place says only that the number is missing.
            <span className={`${String(styles.chosenCredits)} ${creditsGate.reveal}`}>
              <span aria-hidden="true">—</span>
              <span className={hidden.srOnly}>{t('web:booking.actions.creditsFailed')}</span>
            </span>
          ) : standing.kind !== 'standing' || standing.standing === null ? null : (
            <span className={`${String(styles.chosenCredits)} ${creditsGate.reveal}`}>
              {standing.standing.kind === 'owed'
                ? t('web:booking.credit.standingOwed', { count: standing.standing.count })
                : t('web:booking.credit.tokens', { count: standing.standing.count })}
            </span>
          )}
          <button
            type="button"
            className={styles.textButton}
            aria-label={`${t('web:booking.create.change')}: ${t('web:booking.create.chosenClient', { name: chosen.label })}`}
            onClick={() => {
              setChanging(true);
            }}
          >
            {t('web:booking.create.change')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${String(styles.field)} ${String(styles.picker)}`} data-field="client">
      <span className={styles.label}>{t('calendar.create.clientLabel')}</span>
      <PickerList
        copy="web:booking.create.client"
        status={
          roster.isError && roster.data === undefined
            ? 'error'
            : roster.data === undefined
              ? 'pending'
              : 'ready'
        }
        rows={matchingClients(clients, query)}
        total={clients.length}
        query={query}
        onQuery={setQuery}
        onRetry={() => {
          void roster.refetch();
        }}
        selectedId={clientId}
        onSelect={(id) => {
          onChoose(id);
          setChanging(false);
          setQuery('');
        }}
        locked={false}
        reasonId={clientId === null ? reasonId : null}
        invalid={clientId === null && reasonId !== null}
        idOf={(client) => client.customerId}
        renderRow={(client) => (
          <span className={choice.rowText}>
            <span className={choice.rowTitle}>{client.label}</span>
            {client.email !== null && <span className={choice.rowMeta}>{client.email}</span>}
          </span>
        )}
      />
    </div>
  );
}
